/**
 * Generic entity table + form dialog for admin CRUD.
 *
 * Config shape:
 * {
 *   title, addLabel,
 *   listEndpoint, listMapper?, columns: [{key, label, render?}], keyField,
 *   createEndpoint?, updateEndpoint(item), deleteEndpoint(item)?,
 *   fields: [{name, label, type, options?, required?, help?, defaultValue?}],
 *   fromItem(item): initialFormData, toPayload(form, isEdit): payload,
 * }
 */
import { useEffect, useState, useMemo } from "react";
import { client } from "../../lib/config";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";

function FieldRenderer({ field, value, onChange }) {
  const shared = {
    id: field.name,
    "data-testid": `field-${field.name}`,
    value: value ?? "",
    onChange: (e) => onChange(field.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value),
    required: field.required,
    className: "mt-1",
  };
  if (field.type === "textarea" || field.type === "markdown") {
    return <Textarea {...shared} rows={field.rows || 6} />;
  }
  if (field.type === "select") {
    return (
      <select {...shared} className="mt-1 w-full px-3 py-2 border border-[#E5DFD3] rounded-sm bg-white text-sm">
        <option value="">— select —</option>
        {field.options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    );
  }
  if (field.type === "boolean") {
    return (
      <label className="mt-2 flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          data-testid={`field-${field.name}`}
        />
        <span>{field.checkboxLabel || "Yes"}</span>
      </label>
    );
  }
  if (field.type === "tags") {
    return (
      <Input
        {...shared}
        value={Array.isArray(value) ? value.join(", ") : (value ?? "")}
        onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
        placeholder="comma separated"
      />
    );
  }
  return <Input type={field.type || "text"} {...shared} />;
}

export default function AdminEntity({ config }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null); // null = closed, {} = new, item = edit
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const reload = () => {
    setLoading(true);
    client.get(config.listEndpoint)
      .then((r) => setItems(config.listMapper ? config.listMapper(r.data) : r.data))
      .catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  };

  useEffect(reload, [config.listEndpoint]);

  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.trim().toLowerCase();
    return items.filter((it) => JSON.stringify(it).toLowerCase().includes(q));
  }, [items, query]);

  const openNew = () => {
    const initial = {};
    config.fields.forEach((f) => { initial[f.name] = f.defaultValue ?? (f.type === "boolean" ? false : ""); });
    setForm(initial);
    setErrors({});
    setEditing({});
  };

  const openEdit = (item) => {
    setForm(config.fromItem ? config.fromItem(item) : item);
    setErrors({});
    setEditing(item);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});
    const isEdit = editing && Object.keys(editing).length > 0;
    const payload = config.toPayload ? config.toPayload(form, isEdit) : form;
    try {
      if (isEdit) {
        await client.put(config.updateEndpoint(editing), payload);
        toast.success("Saved");
      } else {
        await client.post(config.createEndpoint, payload);
        toast.success("Created");
      }
      setEditing(null);
      reload();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (Array.isArray(d)) {
        const map = {};
        d.forEach((it) => { const loc = it.loc?.slice(-1)[0]; if (loc) map[loc] = it.msg; });
        setErrors(map);
        toast.error("Please fix the highlighted fields");
      } else {
        toast.error(typeof d === "string" ? d : "Save failed");
      }
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!config.deleteEndpoint) return;
    if (!window.confirm("Delete this item? This cannot be undone.")) return;
    try {
      await client.delete(config.deleteEndpoint(item));
      toast.success("Deleted");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="wb-eyebrow">{config.eyebrow || "Admin"}</div>
          <h1 className="wb-h2 mt-2">{config.title}</h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4A4641]" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              data-testid={`${config.testidPrefix}-search`}
              className="pl-10 pr-4 py-2 border border-[#E5DFD3] rounded-sm bg-white text-sm focus:border-[#E85D26] focus:outline-none"
            />
          </div>
          {config.createEndpoint && (
            <button onClick={openNew} className="wb-btn-primary text-sm py-2" data-testid={`${config.testidPrefix}-add`}>
              <Plus className="h-4 w-4" /> {config.addLabel || "Add"}
            </button>
          )}
        </div>
      </div>

      <div className="wb-card bg-white overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-[#4A4641]">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-[#4A4641]">Nothing here yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid={`${config.testidPrefix}-table`}>
              <thead className="bg-[#F5F0E6] text-left" style={{ fontFamily: "'Nunito', sans-serif" }}>
                <tr>
                  {config.columns.map((c) => (
                    <th key={c.key} className="px-4 py-3 font-bold text-[#1A1A1A]">{c.label}</th>
                  ))}
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it, i) => (
                  <tr key={it[config.keyField] || i} className={i % 2 === 0 ? "bg-white" : "bg-[#FDFBF7]"}
                      data-testid={`${config.testidPrefix}-row-${it[config.keyField]}`}>
                    {config.columns.map((c) => (
                      <td key={c.key} className="px-4 py-3 align-top text-[#1A1A1A]">
                        {c.render ? c.render(it) : String(it[c.key] ?? "")}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {config.updateEndpoint && (
                        <button onClick={() => openEdit(it)} className="p-2 text-[#4A4641] hover:text-[#B94A15]"
                                data-testid={`${config.testidPrefix}-edit-${it[config.keyField]}`} aria-label="Edit">
                          <Pencil className="h-4 w-4" />
                        </button>
                      )}
                      {config.deleteEndpoint && (
                        <button onClick={() => remove(it)} className="p-2 text-[#4A4641] hover:text-[#B93D12]"
                                data-testid={`${config.testidPrefix}-delete-${it[config.keyField]}`} aria-label="Delete">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={editing !== null} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-3xl w-[95vw] max-h-[92vh] overflow-y-auto bg-[#FDFBF7] border-[#E5DFD3]"
                       data-testid={`${config.testidPrefix}-dialog`}>
          <DialogHeader>
            <DialogTitle>
              {editing && Object.keys(editing).length > 0 ? `Edit ${config.singular || "item"}` : `New ${config.singular || "item"}`}
            </DialogTitle>
            <DialogDescription>Fill the fields below.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            {config.fields.map((f) => (
              <div key={f.name}>
                {f.type !== "boolean" && <Label htmlFor={f.name}>{f.label}{f.required && " *"}</Label>}
                <FieldRenderer field={f} value={form[f.name]} onChange={(v) => setForm({ ...form, [f.name]: v })} />
                {f.help && <div className="text-xs text-[#4A4641] mt-1">{f.help}</div>}
                {errors[f.name] && <div className="text-xs text-[#B93D12] mt-1">{errors[f.name]}</div>}
              </div>
            ))}
            <DialogFooter className="pt-2">
              <button type="button" onClick={() => setEditing(null)} className="wb-btn-ghost" data-testid={`${config.testidPrefix}-cancel`}>Cancel</button>
              <button type="submit" disabled={saving} className="wb-btn-primary" data-testid={`${config.testidPrefix}-save`}>
                {saving ? "Saving…" : "Save"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
